import type { DomainEvent, SyncPullResponse, SyncPushResponse } from '@pms/shared';

import { authHeaders } from '../auth/session.js';

/**
 * Обращения к серверу.
 *
 * Разделение ошибок здесь принципиальное:
 *   NetworkError  — связи нет или сервер сломался. Повторяем с задержкой,
 *                   записи остаются в очереди.
 *   RequestError  — сервер сказал «так нельзя» (4xx). Повторять бессмысленно,
 *                   иначе очередь встанет навсегда. Помечаем и показываем человеку.
 */

export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export class RequestError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'RequestError';
    this.status = status;
  }
}

/**
 * Сессия кончилась или её отозвали. Это не отказ по существу: повторять после
 * входа имеет смысл, поэтому очередь трогать нельзя — иначе смена, сданная
 * с просроченным токеном, была бы помечена отклонённой и потеряна.
 */
export class AuthRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthRequiredError';
  }
}

/** На слабой мобильной сети запрос может висеть бесконечно — обрываем сами. */
const PUSH_TIMEOUT_MS = 20_000;
const PULL_TIMEOUT_MS = 30_000;

export async function request<T>(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  // Токен подставляется здесь, а не в каждом вызове: забыть его в одном месте
  // означало бы тихо отправлять запросы без входа
  const headers = { ...(await authHeaders()), ...(init.headers as Record<string, string>) };

  let response: Response;
  try {
    response = await fetch(url, { ...init, headers, signal: controller.signal });
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    throw new NetworkError(`Сервер недоступен: ${reason}`);
  } finally {
    clearTimeout(timer);
  }

  if (response.status >= 500) {
    throw new NetworkError(`Сервер ответил ${response.status}`);
  }
  if (response.status === 401) {
    throw new AuthRequiredError('Нужно войти заново');
  }
  if (!response.ok) {
    let message = `Запрос отклонён (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // тело может быть не JSON — сообщения по умолчанию достаточно
    }
    throw new RequestError(response.status, message);
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new NetworkError('Сервер вернул нечитаемый ответ');
  }
}

export function pushBatch(events: DomainEvent[], deviceId: string): Promise<SyncPushResponse> {
  return request<SyncPushResponse>(
    '/api/sync/push',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Id': deviceId,
      },
      body: JSON.stringify({ events }),
    },
    PUSH_TIMEOUT_MS,
  );
}

export function pullSince(cursor: string, deviceId: string): Promise<SyncPullResponse> {
  const params = new URLSearchParams({ since: cursor });
  return request<SyncPullResponse>(
    `/api/sync/pull?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'X-Device-Id': deviceId },
    },
    PULL_TIMEOUT_MS,
  );
}
